/**
 * Kalıcı müzik oynatıcı (Tasarım V4, Öneri 7).
 *
 * ## Neden ayrı bir kanca?
 *
 * Müzik şu ana kadar `WatchScreen` içinde video sahnesiyle çalıyordu; başka
 * bir ekrana geçince bileşen sökülüyor ve müzik duruyordu. Bu kanca, uygulama
 * kabuğunda (App) yaşayan **tek bir `<audio>`** öğesini yönetir. Rota
 * değişse de ses devam eder; mini oynatıcı ve tam ekran "Şimdi Çalıyor"
 * yüzeyi aynı durumu paylaşır.
 *
 * ## Sunucu sözleşmesi
 *
 * Kaynak `/api/items/:id/stream` (HTTP Range). İzleme oturumu
 * `startSession` / `reportProgress` ile yürür; parça bitince `nextItem`
 * yalnız `relation === 'track'` ise sıradakine geçer.
 *
 * ## Otomatik oynatma
 *
 * İlk `play()` bir kullanıcı tıklamasından sonra çağrılır; ancak `api.item`
 * beklediği için tarayıcı jest bağlamını kaybedebilir. `play()` reddedilirse
 * `needsGesture` işaretlenir; mini oynatıcıdaki play düğmesi (gerçek jest)
 * oynatmayı başlatır. Sessizce takılı kalmaz.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Capabilities, type Item, type Library } from './api.ts';
import { apiOrigin } from './apiBase.ts';
import {
  type AudioQueue,
  appendToQueue,
  moveQueueItem as moveQueueItemInState,
  nextQueueIndex,
  normalizeQueue,
  previousQueueIndex,
  queueFromAlbum,
  type RepeatMode,
  removeQueueItem,
} from './audioQueue.ts';
import { clientProfileId } from './deviceProfile.ts';
import { mediaArtworkUrl } from './mediaToken.ts';
import {
  getNativeAudioElement,
  isNativeAudio,
  loadNativeQueue,
  type NativeQueueItem,
  nativeAddQueueItem,
  nativeMoveQueueItem,
  nativeNext,
  nativePrevious,
  nativeRemoveQueueItem,
  nativeSetRepeat,
  nativeSetShuffle,
} from './nativeAudio.ts';
import { loadPlayerPrefs, savePlayerPrefs } from './playerPrefs.ts';
import { resumePositionMs } from './resume.ts';
import { useMediaSession } from './useMediaSession.ts';

export interface AudioPlayerApi {
  current: Item | null;
  isPlaying: boolean;
  isBuffering: boolean;
  /** Tarayıcı otomatik oynatmayı engelledi; kullanıcı play'e basmalı. */
  needsGesture: boolean;
  error: string | null;
  positionMs: number;
  durationMs: number;
  volume: number;
  muted: boolean;
  rate: number;
  /** Yürürlükteki çalma sırası; `current` her zaman bu listenin bir üyesidir. */
  queue: Item[];
  queueIndex: number;
  shuffle: boolean;
  repeat: RepeatMode;
  /** `<audio ref={attach} />` için geri çağırma referansı. */
  attach: (node: HTMLAudioElement | null) => void;
  play: (item: Item, queue?: Item[]) => void;
  toggle: () => void;
  pause: () => void;
  next: () => void;
  previous: () => void;
  playQueueIndex: (index: number) => void;
  enqueue: (item: Item) => void;
  removeFromQueue: (itemId: string) => void;
  clearQueue: () => void;
  moveQueueItem: (from: number, to: number) => void;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  seekTo: (ms: number) => void;
  setVolume: (value: number) => void;
  toggleMute: () => void;
  setRate: (value: number) => void;
  close: () => void;
}

/** Müzik kütüphanesi mi? Ad da tür kadar güvenilir kabul edilir. */
function isMusicLibrary(library: Library): boolean {
  const lower = library.name.toLowerCase();
  return (
    library.kind === 'music' ||
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music')
  );
}

const DEFAULT_REPORT_INTERVAL_MS = 10_000;

export function useAudioPlayer(
  capabilities: Capabilities | null,
  libraries: Library[] | null = null,
): AudioPlayerApi {
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const attach = useCallback((node: HTMLAudioElement | null) => {
    if (isNativeAudio) {
      setAudio(getNativeAudioElement());
      return;
    }
    setAudio(node);
  }, []);

  const [current, setCurrent] = useState<Item | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMutedState] = useState(false);
  const [rate, setRateState] = useState(1);
  const [queue, setQueueState] = useState<AudioQueue>({ items: [], index: -1 });
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');

  const currentRef = useRef<Item | null>(null);
  const queueRef = useRef<AudioQueue>(queue);
  const shuffleRef = useRef(shuffle);
  const repeatRef = useRef(repeat);
  const queueTokenRef = useRef(0);
  const sessionRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  /** Yeni kaynak yüklendiğinde uygulanacak "kaldığın yer" (saniye). */
  const pendingResumeRef = useRef(0);
  const nextRef = useRef<(reason: 'manual' | 'ended') => void>(() => undefined);
  const nativeQueueRequestRef = useRef(0);
  const lastPositionRef = useRef(0);
  /**
   * Native kuyruktaki öğeler ve imzalı biletleri (öğe kimliğiyle).
   *
   * Kuyruk düzenlemesinde **her seferinde bütün kuyruğun bileti yeniden
   * isteniyordu** (168 öğe = 168 istek). Bu önbellek yalnız **yeni** eklenen
   * öğe için bilet ister.
   */
  const nativeItemsRef = useRef<Map<string, NativeQueueItem>>(new Map());

  const reportIntervalMs = capabilities?.progress.reportIntervalMs ?? DEFAULT_REPORT_INTERVAL_MS;

  const commitQueue = useCallback((next: AudioQueue) => {
    queueRef.current = next;
    setQueueState(next);
  }, []);

  useEffect(() => {
    shuffleRef.current = shuffle;
  }, [shuffle]);

  useEffect(() => {
    repeatRef.current = repeat;
  }, [repeat]);

  /** Açık izleme oturumunu `stopped` ile kapatır; konum sunucuya yazılır. */
  const stopSession = useCallback(
    (positionOverride?: number) => {
      const sessionId = sessionRef.current;
      if (sessionId === null) return;
      sessionRef.current = null;
      const position =
        positionOverride ??
        (audio === null ? lastPositionRef.current : Math.round(audio.currentTime * 1000));
      void api
        .reportProgress(sessionId, {
          sessionPositionMs: 0,
          sourcePositionMs: position,
          state: 'stopped',
        })
        .catch(() => undefined);
    },
    [audio],
  );

  const startSession = useCallback((item: Item, resumeMs: number) => {
    void api
      .startSession(item.id, clientProfileId(), resumeMs)
      .then((session) => {
        sessionRef.current = session.sessionId;
        startedAtRef.current = Date.now();
      })
      .catch(() => {
        sessionRef.current = null;
      });
  }, []);

  /**
   * Bir öğe için native kuyruk girdisi üretir.
   *
   * Bilet **önbellekten** gelir; yoksa yalnız bu öğe için sunucudan istenir.
   */
  const nativeItemFor = useCallback(async (entry: Item): Promise<NativeQueueItem> => {
    const cached = nativeItemsRef.current.get(entry.id);
    if (cached !== undefined) return cached;

    const ticket = await api.nativeTicket(entry.id, clientProfileId());
    const nativeItem: NativeQueueItem = {
      itemId: entry.id,
      url: new URL(ticket.url, apiOrigin()).toString(),
      progressToken: ticket.progressToken,
      artworkUrl: mediaArtworkUrl(entry.albumCoverUrl ?? entry.posterUrl ?? entry.thumbnailUrl),
      title: entry.title,
      artist: entry.music?.artist ?? null,
      album: entry.music?.album ?? null,
      durationMs: entry.durationMs,
    };
    nativeItemsRef.current.set(entry.id, nativeItem);
    return nativeItem;
  }, []);

  /**
   * Web queue'sini native Media3 kuyruğuna dönüştürür.
   *
   * Tüm parçalar için toplu imzalı URL alınır; böylece uygulama arka planda/
   * kilitliyken de Media3 sıradaki parçaya kendi başına geçebilir.
   */
  const loadNativeQueueFor = useCallback(
    async (
      nextQueue: AudioQueue,
      item: Item,
      positionMs: number,
      autoplay = true,
    ): Promise<void> => {
      if (!isNativeAudio || nextQueue.items.length === 0) return;
      const request = ++nativeQueueRequestRef.current;
      try {
        const { tickets } = await api.nativeTickets(
          nextQueue.items.map((entry) => entry.id),
          clientProfileId(),
        );
        if (request !== nativeQueueRequestRef.current || currentRef.current?.id !== item.id) {
          return;
        }

        const byId = new Map(tickets.map((ticket) => [ticket.itemId, ticket]));
        const nativeItems: NativeQueueItem[] = [];
        for (const entry of nextQueue.items) {
          const ticket = byId.get(entry.id);
          if (ticket === undefined) continue;
          const nativeItem: NativeQueueItem = {
            itemId: entry.id,
            url: new URL(ticket.url, apiOrigin()).toString(),
            progressToken: ticket.progressToken,
            artworkUrl: mediaArtworkUrl(
              entry.albumCoverUrl ?? entry.posterUrl ?? entry.thumbnailUrl,
            ),
            title: entry.title,
            artist: entry.music?.artist ?? null,
            album: entry.music?.album ?? null,
            durationMs: entry.durationMs,
          };
          nativeItemsRef.current.set(entry.id, nativeItem);
          nativeItems.push(nativeItem);
        }
        const index = nativeItems.findIndex((entry) => entry.itemId === item.id);
        if (index < 0) throw new Error('native_queue_item_missing');
        loadNativeQueue(
          nativeItems,
          index,
          positionMs,
          shuffleRef.current,
          repeatRef.current,
          autoplay,
        );
      } catch {
        setError('Parça oynatılamadı.');
        setIsPlaying(false);
      }
    },
    [],
  );

  const startItem = useCallback(
    (item: Item, nextQueue: AudioQueue) => {
      if (audio === null) return;
      // Önceki parçanın oturumunu **kaynağı değiştirmeden** kapat.
      stopSession();
      queueTokenRef.current += 1;

      const library = libraries?.find((entry) => entry.id === item.libraryId);
      const resumeMs = resumePositionMs(item, {
        ignoreWatchedForMusic: library !== undefined && isMusicLibrary(library),
      });
      currentRef.current = item;
      setCurrent(item);
      commitQueue(nextQueue);
      setError(null);
      setNeedsGesture(false);
      setPositionMs(resumeMs);
      lastPositionRef.current = resumeMs;
      setDurationMs(item.durationMs ?? 0);
      pendingResumeRef.current = resumeMs / 1000;

      if (isNativeAudio) {
        void loadNativeQueueFor(nextQueue, item, resumeMs);
      } else {
        audio.src = api.streamUrl(item.id);
        audio.load();
        void audio.play().catch(() => {
          // Jest bağlamı kayboldu ya da tarayıcı otomatik oynatmayı engelledi.
          setNeedsGesture(true);
        });
      }
      startSession(item, resumeMs);
    },
    [audio, startSession, stopSession, commitQueue, loadNativeQueueFor, libraries],
  );

  /**
   * Müzik kütüphanesindeki parçaları sıraya koyar.
   *
   * ## Kapsam: albüm
   *
   * Sıra **albümle** sınırlıdır: tıklanan parça ve aynı albümün devamı.
   *
   * Önceden kütüphanenin **tamamı** (200'e kadar parça) sıraya giriyordu.
   * Bu iki sorun doğuruyordu: sıra paneli yüzlerce parçayla doluyordu ve
   * "sonraki" komutu kütüphanenin alfabetik sırasında ilerliyordu — yani
   * albüm dinlenmiyordu. Sunucunun "sıradaki parça" çözümü de artık albüm
   * sırasına göre çalışır (`catalog/next.ts`); iki taraf aynı kuralı konuşur.
   *
   * Albüm bilinmiyorsa kütüphane **dökülmez**: sırada yalnız o parça kalır,
   * devamı sunucunun ilişki çözümüne bırakılır.
   */
  const loadQueueFor = useCallback(
    (item: Item) => {
      const library = libraries?.find((entry) => entry.id === item.libraryId);
      if (library === undefined || !isMusicLibrary(library)) return;

      const token = ++queueTokenRef.current;
      const albumId = item.albumId ?? null;
      if (albumId === null) return;

      void api
        .musicAlbum(albumId)
        .then(({ tracks }) => {
          if (token !== queueTokenRef.current || currentRef.current?.id !== item.id) return;
          const nextQueue = normalizeQueue(queueFromAlbum(tracks, item.id), item);
          commitQueue(nextQueue);
          if (isNativeAudio) {
            // İlk native yükleme henüz tamamlanmamış olabilir; audio.currentTime
            // bu anda 0'dır. Kayıtlı devam konumunu kullan.
            void loadNativeQueueFor(
              nextQueue,
              item,
              resumePositionMs(item, { ignoreWatchedForMusic: true }),
            );
          }
        })
        .catch(() => undefined);
    },
    [libraries, commitQueue, loadNativeQueueFor],
  );

  const play = useCallback(
    (item: Item, providedQueue?: Item[]) => {
      if (audio === null) return;
      const initial = normalizeQueue(providedQueue ?? [item], item);
      startItem(item, initial);
      if (providedQueue === undefined) loadQueueFor(item);
    },
    [audio, startItem, loadQueueFor],
  );

  const restartCurrent = useCallback(() => {
    const item = currentRef.current;
    if (audio === null || item === null) return;
    stopSession();
    pendingResumeRef.current = 0;
    audio.currentTime = 0;
    lastPositionRef.current = 0;
    setPositionMs(0);
    void audio.play().catch(() => setNeedsGesture(true));
    startSession(item, 0);
  }, [audio, startSession, stopSession]);

  const advance = useCallback(
    (reason: 'manual' | 'ended') => {
      const item = currentRef.current;
      if (item === null) return;

      if (isNativeAudio && reason === 'manual') {
        nativeNext();
        return;
      }

      if (reason === 'ended' && repeatRef.current === 'one') {
        restartCurrent();
        return;
      }

      const index = nextQueueIndex(queueRef.current, repeatRef.current, shuffleRef.current);
      if (index !== null) {
        const nextItem = queueRef.current.items[index];
        if (nextItem !== undefined) {
          startItem(nextItem, { ...queueRef.current, index });
          return;
        }
      }

      // Sıra henüz yüklenmediyse sunucu çözümüne düş; sonucu sıraya ekle.
      void api
        .nextItem(item.id)
        .then((result) => {
          if (result.item !== null && result.relation === 'track') {
            const nextQueue = normalizeQueue([...queueRef.current.items, result.item], result.item);
            startItem(result.item, nextQueue);
          } else {
            setIsPlaying(false);
          }
        })
        .catch(() => setIsPlaying(false));
    },
    [restartCurrent, startItem],
  );

  useEffect(() => {
    nextRef.current = (reason) => advance(reason);
  }, [advance]);

  const toggle = useCallback(() => {
    if (audio === null || currentRef.current === null) return;
    if (audio.paused) {
      void audio.play().catch(() => setNeedsGesture(true));
    } else {
      audio.pause();
    }
  }, [audio]);

  const pause = useCallback(() => {
    audio?.pause();
  }, [audio]);

  const seekTo = useCallback(
    (ms: number) => {
      if (audio === null) return;
      const target = Math.max(0, ms / 1000);
      audio.currentTime = target;
      lastPositionRef.current = Math.round(target * 1000);
      setPositionMs(target * 1000);
    },
    [audio],
  );

  const setVolume = useCallback(
    (value: number) => {
      if (audio === null) return;
      const next = Math.min(1, Math.max(0, value));
      audio.volume = next;
      if (next > 0) audio.muted = false;
    },
    [audio],
  );

  const toggleMute = useCallback(() => {
    if (audio === null) return;
    audio.muted = !audio.muted;
  }, [audio]);

  const setRate = useCallback(
    (value: number) => {
      if (audio === null) return;
      audio.playbackRate = value;
    },
    [audio],
  );

  const playQueueIndex = useCallback(
    (index: number) => {
      const nextItem = queueRef.current.items[index];
      if (nextItem === undefined) return;
      startItem(nextItem, { ...queueRef.current, index });
    },
    [startItem],
  );

  const enqueue = useCallback(
    (item: Item) => {
      if (currentRef.current === null) {
        play(item);
        return;
      }
      const nextQueue = appendToQueue(queueRef.current, item);
      if (nextQueue === queueRef.current) return;
      commitQueue(nextQueue);
      if (!isNativeAudio) return;

      // Native: kuyruğu **yeniden kurmadan** tek öğe ekle (oynatma kesilmez).
      const index = nextQueue.items.length - 1;
      void nativeItemFor(item)
        .then((nativeItem) => {
          nativeAddQueueItem(nativeItem, index);
        })
        .catch(() => undefined);
    },
    [commitQueue, play, nativeItemFor],
  );

  const removeFromQueue = useCallback(
    (itemId: string) => {
      // Çalan parçayı listeden sessizce silmek, oynatmayı belirsiz bırakır;
      // kullanıcı önce başka bir parçaya geçmeli.
      if (itemId === currentRef.current?.id) return;
      const index = queueRef.current.items.findIndex((entry) => entry.id === itemId);
      const nextQueue = removeQueueItem(queueRef.current, itemId);
      if (nextQueue === queueRef.current) return;
      commitQueue(nextQueue);
      if (!isNativeAudio) return;

      // Native: yalnız o öğe kaldırılır; çalan parça sökülmez, ses kesilmez.
      nativeItemsRef.current.delete(itemId);
      if (index >= 0) nativeRemoveQueueItem(index, itemId);
    },
    [commitQueue],
  );

  const clearQueue = useCallback(() => {
    const item = currentRef.current;
    const previous = queueRef.current;
    const nextQueue = item === null ? { items: [], index: -1 } : { items: [item], index: 0 };
    commitQueue(nextQueue);
    if (!isNativeAudio || item === null) return;

    // Native: çalan parça kalsın, gerisi kalksın (yüksekten aşağı sırayla,
    // böylece indeksler kaymaz).
    for (let index = previous.items.length - 1; index >= 0; index -= 1) {
      const entry = previous.items[index];
      if (entry === undefined || entry.id === item.id) continue;
      nativeItemsRef.current.delete(entry.id);
      nativeRemoveQueueItem(index, entry.id);
    }
  }, [commitQueue]);

  const moveQueueItem = useCallback(
    (from: number, to: number) => {
      const nextQueue = moveQueueItemInState(queueRef.current, from, to);
      if (nextQueue === queueRef.current) return;
      commitQueue(nextQueue);
      if (!isNativeAudio) return;
      nativeMoveQueueItem(from, to);
    },
    [commitQueue],
  );

  const toggleShuffle = useCallback(() => {
    setShuffle((value) => !value);
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeat((value) => (value === 'off' ? 'all' : value === 'all' ? 'one' : 'off'));
  }, []);

  useEffect(() => {
    if (isNativeAudio) nativeSetShuffle(shuffle);
  }, [shuffle]);

  useEffect(() => {
    if (isNativeAudio) nativeSetRepeat(repeat);
  }, [repeat]);

  const previous = useCallback(() => {
    if (audio === null) return;
    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      setPositionMs(0);
      return;
    }
    if (isNativeAudio) {
      nativePrevious();
      return;
    }
    const index = previousQueueIndex(queueRef.current, repeatRef.current);
    if (index === null) {
      audio.currentTime = 0;
      setPositionMs(0);
      return;
    }
    const previousItem = queueRef.current.items[index];
    if (previousItem !== undefined) {
      startItem(previousItem, { ...queueRef.current, index });
    }
  }, [audio, startItem]);

  const close = useCallback(() => {
    stopSession();
    queueTokenRef.current += 1;
    if (audio !== null) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    currentRef.current = null;
    setCurrent(null);
    setIsPlaying(false);
    setIsBuffering(false);
    setPositionMs(0);
    setDurationMs(0);
    commitQueue({ items: [], index: -1 });
    setShuffle(false);
    setRepeat('off');
  }, [audio, stopSession, commitQueue]);

  useEffect(() => {
    if (audio === null) return;

    const prefs = loadPlayerPrefs();
    audio.volume = prefs.volume;
    audio.muted = prefs.muted;
    audio.playbackRate = prefs.rate;
    setVolumeState(prefs.volume);
    setMutedState(prefs.muted);
    setRateState(prefs.rate);

    const onTime = (): void => {
      lastPositionRef.current = Math.round(audio.currentTime * 1000);
      setPositionMs(audio.currentTime * 1000);
    };
    const onNativeTrack = (event: Event): void => {
      const detail = (event as CustomEvent<{ itemId: string; index: number }>).detail;
      /*
       * Eşleştirme **kimlikle** yapılır, indeksle değil.
       *
       * Cerrahi düzenlemeden sonra web kuyruğu ile native kuyruk arasında
       * (bileti alınamayan öğe gibi nedenlerle) bir kayma olabilir; native
       * olayı parçanın kimliğini taşıdığı için ona güveniyoruz.
       */
      const index = queueRef.current.items.findIndex((entry) => entry.id === detail.itemId);
      if (index < 0) return;
      const nextItem = queueRef.current.items[index];
      if (nextItem === undefined) return;
      if (currentRef.current?.id === nextItem.id) {
        commitQueue({ ...queueRef.current, index });
        return;
      }
      stopSession(lastPositionRef.current);
      currentRef.current = nextItem;
      setCurrent(nextItem);
      commitQueue({ ...queueRef.current, index });
      setError(null);
      setNeedsGesture(false);
      setPositionMs(0);
      lastPositionRef.current = 0;
      setDurationMs(nextItem.durationMs ?? 0);
      pendingResumeRef.current = 0;
      startSession(nextItem, 0);
    };
    const onLoaded = (): void => {
      if (pendingResumeRef.current > 0) {
        const limit = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;
        audio.currentTime =
          limit === null ? pendingResumeRef.current : Math.min(pendingResumeRef.current, limit);
        pendingResumeRef.current = 0;
      }
      setDurationMs(
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : 0,
      );
    };
    const onPlay = (): void => {
      setIsPlaying(true);
      setNeedsGesture(false);
      setError(null);
    };
    const onPause = (): void => setIsPlaying(false);
    const onWaiting = (): void => setIsBuffering(true);
    const onPlaying = (): void => setIsBuffering(false);
    const onEnded = (): void => {
      setIsPlaying(false);
      stopSession();
      // Native Media3 kuyruğu sıradaki parçaya kendisi geçer; web'in ikinci
      // kez `advance` çağırması çift yükleme/çift oturum üretir.
      if (isNativeAudio) return;
      nextRef.current('ended');
    };
    const onError = (): void => {
      setError('Parça oynatılamadı.');
      setIsPlaying(false);
      setIsBuffering(false);
    };
    const onVolume = (): void => {
      setVolumeState(audio.volume);
      setMutedState(audio.muted);
      savePlayerPrefs({ volume: audio.volume, muted: audio.muted, rate: audio.playbackRate });
    };
    const onRate = (): void => {
      setRateState(audio.playbackRate);
      savePlayerPrefs({ volume: audio.volume, muted: audio.muted, rate: audio.playbackRate });
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('volumechange', onVolume);
    audio.addEventListener('ratechange', onRate);
    audio.addEventListener('native-track', onNativeTrack);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('volumechange', onVolume);
      audio.removeEventListener('ratechange', onRate);
      audio.removeEventListener('native-track', onNativeTrack);
    };
  }, [audio, stopSession, commitQueue, startSession]);

  useEffect(() => {
    if (audio === null || current === null) return;
    const timer = window.setInterval(() => {
      const sessionId = sessionRef.current;
      if (sessionId === null || audio.paused || audio.ended) return;
      void api
        .reportProgress(sessionId, {
          sessionPositionMs: Date.now() - (startedAtRef.current ?? Date.now()),
          sourcePositionMs: Math.round(audio.currentTime * 1000),
          state: 'playing',
        })
        .catch(() => undefined);
    }, reportIntervalMs);

    return () => {
      window.clearInterval(timer);
      stopSession();
    };
  }, [audio, current, reportIntervalMs, stopSession]);

  /*
   * Medya oturumu yalnız **çalarken** yayınlanır. Böylece bir video
   * açıldığında (audio duraklatılır) eski müzik metadata'sı video oturumunun
   * üzerine yazmaz.
   */
  useMediaSession({
    item: !isNativeAudio && isPlaying ? current : null,
    video: isNativeAudio ? null : audio,
    onNext: () => advance('manual'),
  });

  return {
    current,
    isPlaying,
    isBuffering,
    needsGesture,
    error,
    positionMs,
    durationMs,
    volume,
    muted,
    rate,
    queue: queue.items,
    queueIndex: queue.index,
    shuffle,
    repeat,
    attach,
    play,
    toggle,
    pause,
    next: () => advance('manual'),
    previous,
    playQueueIndex,
    enqueue,
    removeFromQueue,
    clearQueue,
    moveQueueItem,
    toggleShuffle,
    cycleRepeat,
    seekTo,
    setVolume,
    toggleMute,
    setRate,
    close,
  };
}
